import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import SmartScrollRow from "./SmartScrollRow.js";
import { setCurrentCategoryDispatch } from "../../server/Category";
import { isCategoryVisible } from "../../utils/categoryVisibility";
import useCategoryReorder from "./useCategoryReorder";
import "./SubNavbar.scss";

/**
 * Re-computes --shell-fixed-stack-bottom by reading the current bottom edge
 * of every nav.sub-navbar element in the page plus the header wrapper.
 * Called whenever any sub-navbar mounts, resizes, or unmounts so the <main>
 * padding-top stays accurate even when bars appear/disappear asynchronously.
 */
function syncFixedStackBottom() {
  const root = document.documentElement;
  const headerWrap = document.querySelector(".header-wrapper");
  const subnavs = Array.from(document.querySelectorAll("nav.sub-navbar"));
  const headerBottom = headerWrap
    ? headerWrap.getBoundingClientRect().bottom
    : 0;
  const subBottom = subnavs.length
    ? Math.max(...subnavs.map((n) => n.getBoundingClientRect().bottom))
    : 0;
  const fixed = Math.max(0, Math.ceil(Math.max(headerBottom, subBottom)));
  root.style.setProperty("--shell-fixed-stack-bottom", `${fixed}px`);
}

const SubNavbar = ({
  fatherSlug,
  currentSubCategory,
  currentCategories,
  level = 1,
  setPendingScrollSlug,
}) => {
  const { id } = useParams();
  const { pathname } = useLocation();
  const [children, setChildren] = useState(null);
  const [orderedChildren, setOrderedChildren] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const dispatch = useDispatch();
  const navRef = useRef(null);

  const { includeInactive, role, stationId, isClientView } = useSelector(
    (state) => ({
      includeInactive: state.category.includeInactive,
      role: state.user.role,
      stationId: state.user.stationId,
      isClientView: state.category.isClientView,
    }),
  );

  useEffect(() => {
    setChildren(null);
    setCurrentCategory(null);
  }, [pathname]);

  useEffect(() => {
    setOrderedChildren(
      Array.isArray(currentCategories?.children)
        ? currentCategories.children
        : [],
    );
  }, [currentCategories]);

  /* Keep --shell-fixed-stack-bottom in sync whenever this bar mounts/resizes/unmounts */
  useEffect(() => {
    const el = navRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;

    syncFixedStackBottom();

    const ro = new ResizeObserver(() =>
      requestAnimationFrame(syncFixedStackBottom),
    );
    ro.observe(el);

    return () => {
      ro.disconnect();
      /* After unmount, give React one tick to remove the element, then re-measure */
      setTimeout(syncFixedStackBottom, 0);
    };
  }, []);

  const handleCategoryClick = (slug) => {
    setPendingScrollSlug(slug);
  };

  const updateCurr = useCallback(
    (slug) => {
      const curr = currentCategories.children.find((cat) => cat.slug === slug);
      if (curr) {
        if (curr.children.filter((cat) => !cat.isFamily).length !== 0) {
          dispatch(setCurrentCategoryDispatch(curr));
          setChildren(curr);
          setCurrentCategory(curr.slug);
        } else {
          // Keep the leaf highlighted via local currentCategory. Still point Redux
          // at the parent tree so CategoryPage scroll-spy parent checks work.
          dispatch(setCurrentCategoryDispatch(currentCategories));
          setCurrentCategory(curr.slug);
          setChildren(null);
        }
      } else {
        for (let cat of currentCategories.children) {
          for (let subCat of cat.children) {
            if (subCat.slug === slug) {
              dispatch(setCurrentCategoryDispatch(cat));
              setChildren(cat);
              setCurrentCategory(cat.slug);
              return;
            }
          }
        }
        // Slug is this bar's own parent (or unknown) — clear a stale child
        // highlight so e.g. Soft Drinks doesn't leave "תה קר" active on L2.
        const isDescendantSlug = (node, targetSlug) => {
          for (const child of node?.children || []) {
            if (child.slug === targetSlug) return true;
            if (isDescendantSlug(child, targetSlug)) return true;
          }
          return false;
        };

        // If slug is deeper than this bar's grandchild, keep nested bars mounted
        // so level-3 selections don't collapse the hierarchy.
        if (!isDescendantSlug(currentCategories, slug)) {
          setCurrentCategory(null);
          setChildren(null);
        }
      }
    },
    [currentCategories, dispatch],
  );

  useEffect(() => {
    if (currentSubCategory) {
      updateCurr(currentSubCategory);
    }
  }, [currentSubCategory, updateCurr]);

  // Keep the active pill visible inside the horizontal overflow row.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const activeEl = nav.querySelector(".sub-navbar-item.active");
    if (!activeEl) return;

    activeEl.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [currentCategory, currentSubCategory, orderedChildren]);

  const {
    canReorder,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    suppressClickRef,
  } = useCategoryReorder({
    items: orderedChildren,
    setItems: setOrderedChildren,
    enabled: Boolean(role && stationId),
    stationId,
    isClientView: role ? isClientView : true,
    includeInactive: role ? includeInactive : false,
    refreshCategorySlug: pathname.startsWith("/category") ? id : null,
    optimisticParentSlug: currentCategories?.slug,
  });

  return (
    <>
      <nav
        ref={navRef}
        className={`sub-navbar sub-navbar--level-${level}${role ? " sub-navbar--admin" : ""}`}
        aria-label={`תת־קטגוריות רמה ${level}`}
      >
        <SmartScrollRow rtl trackClassName="sub-navbar__scroll-track">
          {orderedChildren.map(
            (category) =>
              isCategoryVisible(category, role, includeInactive) && (
                <a
                  key={category.slug}
                  href="/"
                  className={`sub-navbar-item ${category.slug === currentCategory || category.slug === currentSubCategory ? "active" : ""}`}
                  draggable={canReorder}
                  onDragStart={() => handleDragStart(category.slug)}
                  onDragOver={handleDragOver}
                  onDrop={(event) => handleDrop(event, category.slug)}
                  onDragEnd={handleDragEnd}
                  onClick={(e) => {
                    if (suppressClickRef.current) {
                      e.preventDefault();
                      return;
                    }
                    e.preventDefault();
                    handleCategoryClick(category.slug);
                  }}
                >
                  {category.name}
                </a>
              ),
          )}
        </SmartScrollRow>
      </nav>
      {children &&
        children.children.filter((cat) => !cat.isFamily).length !== 0 && (
          <SubNavbar
            fatherSlug={currentCategory}
            currentSubCategory={currentSubCategory}
            currentCategories={children}
            level={level + 1}
            setPendingScrollSlug={setPendingScrollSlug}
          />
        )}
    </>
  );
};

export default SubNavbar;
